/*
 * Copyright (C) 2026 Motorola Solutions, Inc.
 * All Rights Reserved.
 * Motorola Solutions Confidential Restricted.
 */

import React from 'react';
import { PrimeTableColumn } from './PrimeTable.types';
import { getNestedValue, isSelected } from './PrimeTable.utils';

export interface PrimeTableBodyProps<T = any> {
  paginatedData: T[];
  columns: PrimeTableColumn<T>[];
  rowExpansionTemplate?: (data: T) => React.ReactNode;
  selectionMode?: 'single' | 'multiple' | 'checkbox';
  emptyMessage: string | React.ReactNode;
  first: number;
  dataKey?: string;
  selection: T | T[] | null;
  compareSelectionBy: 'equals' | 'deepEquals';
  isRowExpanded: (item: any) => boolean;
  onRowClick?: (event: { originalEvent: React.MouseEvent; data: T; index: number }) => void;
  handleRowClick: (item: any, event: React.MouseEvent | React.ChangeEvent, rowIndex: number) => void;
  handleRowExpand: (item: any, rowIndex: number) => void;
  getSortedColumnsWithIndices: () => { col: PrimeTableColumn; originalIndex: number }[];
  frozenColumns: Set<string>;
  frozenPositions: { [key: string]: number };
  getColumnWidth: (field: string, defaultWidth?: string, minWidth?: string) => string;
}

export const PrimeTableBody = <T extends any>({
  paginatedData,
  columns,
  rowExpansionTemplate,
  selectionMode,
  emptyMessage,
  first,
  dataKey,
  selection,
  compareSelectionBy,
  isRowExpanded,
  onRowClick,
  handleRowClick,
  handleRowExpand,
  getSortedColumnsWithIndices,
  frozenColumns,
  frozenPositions,
  getColumnWidth,
}: PrimeTableBodyProps<T>) => {
  if (paginatedData.length === 0) {
    return (
      <tbody>
        <tr>
          <td
            colSpan={
              columns.length +
              (rowExpansionTemplate !== undefined ? 1 : 0) +
              (selectionMode === 'checkbox' ? 1 : 0)
            }
            className="empty-message"
          >
            {emptyMessage}
          </td>
        </tr>
      </tbody>
    );
  }

  return (
    <tbody>
      {paginatedData.map((item, rowIndex) => {
        const actualIndex = first + rowIndex;
        const selected = isSelected(item, selection, dataKey, compareSelectionBy);
        const expanded = isRowExpanded(item);
        const sortedColumnsWithIndices = getSortedColumnsWithIndices();

        return (
          <React.Fragment key={dataKey ? getNestedValue(item, dataKey) : rowIndex}>
            <tr
              className={`${selectionMode ? 'selectable' : ''} ${
                rowExpansionTemplate !== undefined ? 'expandable' : ''
              }`}
              onClick={(e) => {
                if (onRowClick) {
                  onRowClick({ originalEvent: e, data: item, index: actualIndex });
                } else if (selectionMode) {
                  handleRowClick(item, e, actualIndex);
                }
              }}
            >
              {rowExpansionTemplate !== undefined && (
                <td className="frozen-column frozen-left" style={{ left: 0 }}>
                  <span
                    className={`expand-icon ${expanded ? 'expanded' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRowExpand(item, actualIndex);
                    }}
                  >
                    ▶
                  </span>
                </td>
              )}
              {selectionMode === 'checkbox' && (
                <td
                  className="frozen-column frozen-left"
                  style={{ left: rowExpansionTemplate !== undefined ? '48px' : 0 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type="checkbox"
                    className="selection-checkbox"
                    checked={selected}
                    onChange={(e) => handleRowClick(item, e as any, actualIndex)}
                  />
                </td>
              )}
              {sortedColumnsWithIndices.map(({ col: column, originalIndex }, displayIndex) => {
                const fieldKey = column.field as string;
                const isFrozen = frozenColumns.has(fieldKey);
                const leftPosition = frozenPositions[fieldKey];
                const columnWidth = getColumnWidth(fieldKey, column.width, column.minWidth);

                return (
                  <td
                    key={fieldKey}
                    className={`${column.className || ''} ${
                      column.align ? `align-${column.align}` : ''
                    } ${isFrozen ? 'frozen-column frozen-left' : ''}`}
                    style={{
                      width: columnWidth,
                      minWidth: columnWidth,
                      maxWidth: columnWidth,
                      ...(isFrozen ? { left: `${leftPosition}px` } : {}),
                    }}
                  >
                    {column.body ? column.body(item, column) : getNestedValue(item, fieldKey)}
                  </td>
                );
              })}
            </tr>
            {expanded && rowExpansionTemplate !== undefined && (
              <tr className="expanded-row">
                <td
                  colSpan={
                    columns.filter((c) => c.visible !== false).length +
                    (rowExpansionTemplate !== undefined ? 1 : 0) +
                    (selectionMode === 'checkbox' ? 1 : 0)
                  }
                >
                  <div className="expanded-content">{rowExpansionTemplate(item)}</div>
                </td>
              </tr>
            )}
          </React.Fragment>
        );
      })}
    </tbody>
  );
};

export default PrimeTableBody;
